import { useCallback, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'

import { cn } from '@/lib/utils'

export type SortDirection = 'asc' | 'desc'

interface SortableHeaderProps {
  active: boolean
  direction: SortDirection
  onToggle: () => void
  children: React.ReactNode
  className?: string
}

export function SortableHeader({
  active,
  direction,
  onToggle,
  children,
  className,
}: SortableHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'flex items-center gap-1 hover:text-foreground transition-colors',
        className,
      )}
    >
      {children}
      {active ? (
        direction === 'asc' ? (
          <ArrowUp className="h-3.5 w-3.5" />
        ) : (
          <ArrowDown className="h-3.5 w-3.5" />
        )
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
      )}
    </button>
  )
}

export function useTableSort<F extends string>(
  initialField: F,
  initialDirection: SortDirection = 'asc',
) {
  const [sortField, setSortField] = useState<F>(initialField)
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialDirection)

  const toggleSort = useCallback((field: F) => {
    setSortField((current) => {
      if (current === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
        return current
      }
      setSortDirection('asc')
      return field
    })
  }, [])

  return { sortField, sortDirection, toggleSort }
}

export function compareStrings(a: string | undefined | null, b: string | undefined | null) {
  const av = a ?? ''
  const bv = b ?? ''
  // Empty values sort last regardless of direction.
  if (!av && bv) return 1
  if (av && !bv) return -1
  return av.localeCompare(bv, undefined, { sensitivity: 'base' })
}

export function compareNumbers(a: number | bigint, b: number | bigint) {
  const an = typeof a === 'bigint' ? Number(a) : a
  const bn = typeof b === 'bigint' ? Number(b) : b
  return an - bn
}
